import {
  Box,
  Container,
  Heading,
  Link as ChakraLink,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  VStack,
  useColorModeValue,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { PREMIUM_PLANS } from "../constants/premiumPlans";
import { APP_FONT_FAMILY } from "../constants/typography";

const INSTAGRAM_URL =
  process.env.REACT_APP_INSTAGRAM_Link?.trim() || "https://www.instagram.com/payinapo/";
const INSTAGRAM_HANDLE = "@payinapo";

function Section({ id, title, children }) {
  const titleColor = useColorModeValue("gray.900", "white");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");

  return (
    <Box
      as="section"
      id={id}
      scrollMarginTop="120px"
      borderTop="1px solid"
      borderColor={border}
      pt={10}
      mt={2}
    >
      <Heading
        as="h2"
        fontSize={{ base: "xl", md: "2xl" }}
        fontWeight="700"
        color={titleColor}
        letterSpacing="-0.02em"
        mb={5}
      >
        {title}
      </Heading>
      <VStack align="stretch" spacing={4}>
        {children}
      </VStack>
    </Box>
  );
}

function SubHeading({ children }) {
  const color = useColorModeValue("gray.800", "whiteAlpha.900");
  return (
    <Heading as="h3" fontSize="md" fontWeight="650" color={color} mt={2}>
      {children}
    </Heading>
  );
}

function TocLink({ to, children }) {
  const linkColor = useColorModeValue("gray.700", "whiteAlpha.800");
  return (
    <WrapItem>
      <ChakraLink as={RouterLink} to={to} color={linkColor} fontSize="sm" fontWeight="500">
        {children}
      </ChakraLink>
    </WrapItem>
  );
}

const TermsGuidelines = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const pageBg = useColorModeValue("transparent", "transparent");
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.600", "whiteAlpha.700");
  const body = useColorModeValue("gray.700", "whiteAlpha.850");
  const cardBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(18,18,20,0.72)");
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const cardShadow = useColorModeValue(
    "0 16px 40px rgba(15,23,42,0.06)",
    "0 16px 40px rgba(0,0,0,0.35)"
  );
  const tocBg = useColorModeValue("brand.50", "whiteAlpha.50");
  const accentLink = useColorModeValue("brand.600", "brand.200");

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const listProps = {
    spacing: 2,
    pl: 1,
    styleType: "disc",
    color: body,
    fontSize: "sm",
    lineHeight: "tall",
  };
  const orderedProps = {
    spacing: 2,
    pl: 1,
    styleType: "decimal",
    color: body,
    fontSize: "sm",
    lineHeight: "tall",
  };

  const monthlyAmount = PREMIUM_PLANS.monthly.amount;
  const yearlyAmount = PREMIUM_PLANS.yearly.amount;
  const yearlyMonthlyEq = PREMIUM_PLANS.yearly.monthlyEquivalent;

  return (
    <Container maxW="3xl" px={{ base: 4, md: 6 }} py={{ base: 2, md: 4 }} bg={pageBg}>
      <Box
        p={{ base: 5, md: 8 }}
        borderRadius="2xl"
        bg={cardBg}
        border="1px solid"
        borderColor={cardBorder}
        boxShadow={cardShadow}
      >
        <Text
          fontSize="xs"
          fontWeight="600"
          letterSpacing="0.08em"
          textTransform="uppercase"
          color={muted}
          mb={2}
        >
          {t("legal.eyebrow")}
        </Text>
        <Heading
          as="h1"
          fontSize={{ base: "2rem", md: "2.5rem" }}
          lineHeight="1.1"
          fontWeight="700"
          color={titleColor}
          letterSpacing="-0.03em"
          mb={3}
        >
          {t("legal.title")}
        </Heading>
        <Text fontSize="sm" color={muted} mb={2}>
          {t("legal.updated", { date: t("legal.updatedDate") })}
        </Text>
        <Text fontSize="sm" color={body} lineHeight="tall" mb={6}>
          {t("legal.intro")}
        </Text>

        <Box mb={8} p={4} borderRadius="xl" bg={tocBg}>
          <Wrap spacingX={3} spacingY={2}>
            <TocLink to="/terms#terms">{t("legal.termsHeading")}</TocLink>
            <Text as="span" color={muted} fontSize="sm">
              ·
            </Text>
            <TocLink to="/terms#aup">{t("legal.aupHeading")}</TocLink>
            <Text as="span" color={muted} fontSize="sm">
              ·
            </Text>
            <TocLink to="/terms#contact">{t("legal.contactHeading")}</TocLink>
            <Text as="span" color={muted} fontSize="sm">
              ·
            </Text>
            <TocLink to="/terms#pricing">{t("legal.pricingHeading")}</TocLink>
            <Text as="span" color={muted} fontSize="sm">
              ·
            </Text>
            <TocLink to="/terms#law-enforcement">{t("legal.lawHeading")}</TocLink>
            <Text as="span" color={muted} fontSize="sm">
              ·
            </Text>
            <TocLink to="/terms#privacy">{t("legal.privacyHeading")}</TocLink>
            <Text as="span" color={muted} fontSize="sm">
              ·
            </Text>
            <TocLink to="/terms#refund">{t("legal.refundHeading")}</TocLink>
            <Text as="span" color={muted} fontSize="sm">
              ·
            </Text>
            <TocLink to="/terms#guidelines">{t("legal.guidelinesHeading")}</TocLink>
          </Wrap>
        </Box>

        <Section id="terms" title={t("legal.termsHeading")}>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.termsIntro")}
          </Text>

          <SubHeading>{t("legal.terms.eligibilityTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.eligibilityBody")}
          </Text>

          <SubHeading>{t("legal.terms.accountTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.accountBody")}
          </Text>

          <SubHeading>{t("legal.terms.contentTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.contentBody")}
          </Text>

          <SubHeading>{t("legal.terms.safetyTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.safetyBody")}
          </Text>

          <SubHeading>{t("legal.terms.exploitationTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.exploitationIntro")}
          </Text>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.terms.exploitation1")}</ListItem>
            <ListItem>{t("legal.terms.exploitation2")}</ListItem>
            <ListItem>{t("legal.terms.exploitation3")}</ListItem>
            <ListItem>{t("legal.terms.exploitation4")}</ListItem>
            <ListItem>{t("legal.terms.exploitation5")}</ListItem>
          </UnorderedList>

          <SubHeading>{t("legal.terms.premiumTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.premiumBody")}
          </Text>

          <SubHeading>{t("legal.terms.terminationTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.terminationBody")}
          </Text>

          <SubHeading>{t("legal.terms.disclaimerTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.disclaimerBody")}
          </Text>

          <SubHeading>{t("legal.terms.changesTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.terms.changesBody")}
          </Text>
        </Section>

        <Section id="aup" title={t("legal.aupHeading")}>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.aupIntro")}
          </Text>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.aup.use1")}</ListItem>
            <ListItem>{t("legal.aup.use2")}</ListItem>
            <ListItem>{t("legal.aup.use3")}</ListItem>
            <ListItem>{t("legal.aup.use4")}</ListItem>
            <ListItem>{t("legal.aup.use5")}</ListItem>
            <ListItem>{t("legal.aup.use6")}</ListItem>
            <ListItem>{t("legal.aup.use7")}</ListItem>
            <ListItem>{t("legal.aup.use8")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.aup.safetyTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.aup.safety1")}</ListItem>
            <ListItem>{t("legal.aup.safety2")}</ListItem>
            <ListItem>{t("legal.aup.safety3")}</ListItem>
            <ListItem>{t("legal.aup.safety4")}</ListItem>
            <ListItem>{t("legal.aup.safety5")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.aup.csamTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.aup.csam1")}</ListItem>
            <ListItem>{t("legal.aup.csam2")}</ListItem>
            <ListItem>{t("legal.aup.csam3")}</ListItem>
            <ListItem>{t("legal.aup.csam4")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.aup.explicitTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.aup.explicit1")}</ListItem>
            <ListItem>{t("legal.aup.explicit2")}</ListItem>
            <ListItem>{t("legal.aup.explicit3")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.aup.scamTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.aup.scam1")}</ListItem>
            <ListItem>{t("legal.aup.scam2")}</ListItem>
            <ListItem>{t("legal.aup.scam3")}</ListItem>
          </UnorderedList>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.aup.enforcement")}
          </Text>
        </Section>

        <Section id="contact" title={t("legal.contactHeading")}>
          <Text fontSize="sm" color={body} lineHeight="tall">
            <Trans
              i18nKey="legal.contactBody"
              values={{ handle: INSTAGRAM_HANDLE }}
              components={{
                instagram: (
                  <ChakraLink
                    href={INSTAGRAM_URL}
                    isExternal
                    color={accentLink}
                    fontWeight="600"
                    textDecoration="underline"
                    textUnderlineOffset="2px"
                  />
                ),
              }}
            />
          </Text>
          <UnorderedList {...listProps}>
            <ListItem>
              <Trans
                i18nKey="legal.contactInstagram"
                values={{ handle: INSTAGRAM_HANDLE }}
                components={{
                  instagram: (
                    <ChakraLink href={INSTAGRAM_URL} isExternal fontWeight="600" />
                  ),
                }}
              />
            </ListItem>
            <ListItem>{t("legal.contactHelp")}</ListItem>
            <ListItem>{t("legal.contactResponse")}</ListItem>
          </UnorderedList>
        </Section>

        <Section id="pricing" title={t("legal.pricingHeading")}>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.pricingIntro")}
          </Text>
          <UnorderedList {...listProps}>
            <ListItem>
              {t("legal.pricing.monthly", { amount: monthlyAmount })}
            </ListItem>
            <ListItem>
              {t("legal.pricing.yearly", {
                amount: yearlyAmount,
                monthly: yearlyMonthlyEq,
              })}
            </ListItem>
            <ListItem>{t("legal.pricing.free")}</ListItem>
            <ListItem>{t("legal.pricing.billing")}</ListItem>
            <ListItem>{t("legal.pricing.changes")}</ListItem>
          </UnorderedList>
        </Section>

        <Section id="law-enforcement" title={t("legal.lawHeading")}>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.lawIntro")}
          </Text>
          <SubHeading>{t("legal.law.requestTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            <Trans
              i18nKey="legal.law.requestBody"
              values={{ handle: INSTAGRAM_HANDLE }}
              components={{
                instagram: (
                  <ChakraLink href={INSTAGRAM_URL} isExternal fontWeight="600" />
                ),
              }}
            />
          </Text>
          <SubHeading>{t("legal.law.intakeTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.law.intake1")}</ListItem>
            <ListItem>{t("legal.law.intake2")}</ListItem>
            <ListItem>{t("legal.law.intake3")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.law.verifyTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.law.verify1")}</ListItem>
            <ListItem>{t("legal.law.verify2")}</ListItem>
            <ListItem>{t("legal.law.verify3")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.law.processTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.law.process1")}</ListItem>
            <ListItem>{t("legal.law.process2")}</ListItem>
            <ListItem>{t("legal.law.process3")}</ListItem>
            <ListItem>{t("legal.law.process4")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.law.dataTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.law.data1")}</ListItem>
            <ListItem>{t("legal.law.data2")}</ListItem>
            <ListItem>{t("legal.law.data3")}</ListItem>
            <ListItem>{t("legal.law.data4")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.law.responseTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.law.response1")}</ListItem>
            <ListItem>{t("legal.law.response2")}</ListItem>
            <ListItem>{t("legal.law.response3")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.law.preserveTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.law.preserveBody")}
          </Text>
          <SubHeading>{t("legal.law.emergencyTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.law.emergencyBody")}
          </Text>
          <SubHeading>{t("legal.law.noticeTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.law.noticeBody")}
          </Text>
          <SubHeading>{t("legal.law.recordsTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.law.recordsBody")}
          </Text>
          <SubHeading>{t("legal.law.intlTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.law.intlBody")}
          </Text>
        </Section>

        <Section id="privacy" title={t("legal.privacyHeading")}>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.privacyIntro")}
          </Text>
          <SubHeading>{t("legal.privacy.collectTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.privacy.collect1")}</ListItem>
            <ListItem>{t("legal.privacy.collect2")}</ListItem>
            <ListItem>{t("legal.privacy.collect3")}</ListItem>
            <ListItem>{t("legal.privacy.collect4")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.privacy.useTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.privacy.use1")}</ListItem>
            <ListItem>{t("legal.privacy.use2")}</ListItem>
            <ListItem>{t("legal.privacy.use3")}</ListItem>
            <ListItem>{t("legal.privacy.use4")}</ListItem>
          </UnorderedList>
          <SubHeading>{t("legal.privacy.shareTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.privacy.shareBody")}
          </Text>
          <SubHeading>{t("legal.privacy.retentionTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.privacy.retentionBody")}
          </Text>
          <SubHeading>{t("legal.privacy.rightsTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.privacy.rightsBody")}
          </Text>
          <SubHeading>{t("legal.privacy.securityTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.privacy.securityBody")}
          </Text>
        </Section>

        <Section id="refund" title={t("legal.refundHeading")}>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.refundIntro")}
          </Text>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.refund.item1")}</ListItem>
            <ListItem>{t("legal.refund.item2")}</ListItem>
            <ListItem>{t("legal.refund.item3")}</ListItem>
            <ListItem>{t("legal.refund.item4")}</ListItem>
            <ListItem>{t("legal.refund.item5")}</ListItem>
          </UnorderedList>
          <Text fontSize="sm" color={body} lineHeight="tall">
            <Trans
              i18nKey="legal.refund.contact"
              values={{ handle: INSTAGRAM_HANDLE }}
              components={{
                instagram: (
                  <ChakraLink href={INSTAGRAM_URL} isExternal fontWeight="600" />
                ),
              }}
            />
          </Text>
        </Section>

        <Section id="guidelines" title={t("legal.guidelinesHeading")}>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.guidelinesIntro")}
          </Text>

          <SubHeading>{t("legal.guidelines.respectTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.guidelines.respect1")}</ListItem>
            <ListItem>{t("legal.guidelines.respect2")}</ListItem>
            <ListItem>{t("legal.guidelines.respect3")}</ListItem>
          </UnorderedList>

          <SubHeading>{t("legal.guidelines.authenticityTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.guidelines.authenticity1")}</ListItem>
            <ListItem>{t("legal.guidelines.authenticity2")}</ListItem>
            <ListItem>{t("legal.guidelines.authenticity3")}</ListItem>
          </UnorderedList>

          <SubHeading>{t("legal.guidelines.safetyTitle")}</SubHeading>
          <UnorderedList {...listProps}>
            <ListItem>{t("legal.guidelines.safety1")}</ListItem>
            <ListItem>{t("legal.guidelines.safety2")}</ListItem>
            <ListItem>{t("legal.guidelines.safety3")}</ListItem>
            <ListItem>{t("legal.guidelines.safety4")}</ListItem>
          </UnorderedList>

          <SubHeading>{t("legal.guidelines.prohibitedTitle")}</SubHeading>
          <OrderedList {...orderedProps}>
            <ListItem>{t("legal.guidelines.prohibited1")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited2")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited3")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited4")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited5")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited6")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited7")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited8")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited9")}</ListItem>
            <ListItem>{t("legal.guidelines.prohibited10")}</ListItem>
          </OrderedList>

          <SubHeading>{t("legal.guidelines.reportingTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.guidelines.reportingBody")}
          </Text>

          <SubHeading>{t("legal.guidelines.enforcementTitle")}</SubHeading>
          <Text fontSize="sm" color={body} lineHeight="tall">
            {t("legal.guidelines.enforcementBody")}
          </Text>
        </Section>

        <Text
          mt={10}
          pt={6}
          borderTop="1px solid"
          borderColor={cardBorder}
          fontSize="xs"
          color={muted}
          fontFamily={APP_FONT_FAMILY}
          fontStyle="italic"
        >
          {t("legal.closing")}
        </Text>
      </Box>
    </Container>
  );
};

export default TermsGuidelines;
